import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { CheckCircle2, Clock, Lock, MapPin, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, ErrorBox, Field, Input, Spinner, useToast, PasswordInput } from '../components/ui';
import { Logo } from '../components/logo';

function AuthShell({ children, title, subtitle }: { children: ReactNode; title: string; subtitle?: ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-gradient-to-br from-brand-950 via-brand-900 to-brand-700 p-12 text-white lg:flex">
        <Logo dark size="lg" />
        <div className="max-w-md space-y-6">
          <h2 className="text-3xl font-semibold leading-tight">Registro horario claro, trazable y preparado para tu empresa.</h2>
          <ul className="space-y-3 text-sm text-brand-100">
            <li className="flex gap-3">
              <Clock className="h-5 w-5 shrink-0" /> Planificado, real y computable siempre diferenciados.
            </li>
            <li className="flex gap-3">
              <ShieldCheck className="h-5 w-5 shrink-0" /> Las correcciones nunca sobrescriben el dato original: todo queda auditado.
            </li>
            <li className="flex gap-3">
              <MapPin className="h-5 w-5 shrink-0" /> GPS solo en el momento del fichaje, nunca seguimiento continuo.
            </li>
          </ul>
        </div>
        <p className="text-xs text-brand-200">WulfTime facilita la gestión del registro de jornada; no sustituye el asesoramiento laboral ni garantiza por sí solo el cumplimiento normativo.</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Logo size="lg" />
          </div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, verifyMfa, me } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me) navigate('/', { replace: true });
  }, [me, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (challenge) await verifyMfa(challenge, code, remember);
      else {
        const r = await login(email, password);
        if (r.mfaRequired) setChallenge(r.challengeToken!);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={challenge ? 'Verificación en dos pasos' : 'Iniciar sesión'} subtitle={challenge ? 'Introduce el código de 6 dígitos de tu aplicación de autenticación o un código de recuperación.' : 'Accede a tu cuenta de WulfTime.'}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <ErrorBox error={error} />
        {!challenge ? (
          <>
            <Field label="Email">{(id) => <Input id={id} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
            <Field label="Contraseña">{(id) => <PasswordInput id={id} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
          </>
        ) : (
          <>
            <Field label="Código">{(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} className="text-center text-lg tracking-[0.3em]" />}</Field>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span>
                No volver a pedir el código en este dispositivo durante 30 días
                <span className="block text-xs text-slate-500">Desmárcalo en ordenadores compartidos. La contraseña se pedirá igualmente.</span>
              </span>
            </label>
          </>
        )}
        <Button type="submit" className="w-full" loading={busy} icon={<Lock className="h-4 w-4" />}>
          {challenge ? 'Verificar' : 'Entrar'}
        </Button>
        {!challenge && (
          <div className="flex justify-between text-sm">
            <Link to="/forgot-password" className="text-brand-700 hover:underline">
              ¿Has olvidado la contraseña?
            </Link>
            <Link to="/signup" className="text-slate-600 hover:underline">
              Crear empresa
            </Link>
          </div>
        )}
        {!challenge && (
          <p className="pt-2 text-center text-xs text-slate-400">
            ¿Tablet de fichaje compartida? <Link to="/kiosk" className="text-brand-700 hover:underline">Abrir modo kiosco</Link>
          </p>
        )}
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <AuthShell title="Recuperar contraseña" subtitle="Te enviaremos un enlace para elegir una nueva contraseña.">
      {sent ? (
        <Alert tone="success">{sent}</Alert>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              setSent((await api.post<{ message: string }>('/api/v1/auth/password/forgot', { email })).message);
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <ErrorBox error={error} />
          <Field label="Email">{(id) => <Input id={id} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
          <Button type="submit" className="w-full" loading={busy}>
            Enviar enlace
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="text-brand-700 hover:underline">
          Volver al inicio de sesión
        </Link>
      </p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const welcome = params.get('welcome') === '1';
  return (
    <AuthShell title={welcome ? 'Bienvenido a WulfTime' : 'Nueva contraseña'} subtitle={`${welcome ? 'Elige tu contraseña para entrar. ' : ''}Mínimo 10 caracteres combinando mayúsculas, minúsculas, números o símbolos.`}>
      {done ? (
        <Alert tone="success" title={welcome ? 'Contraseña creada' : 'Contraseña actualizada'}>
          <Link to="/login" className="underline">
            Inicia sesión
          </Link>{' '}
          con tu nueva contraseña.
        </Alert>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await api.post('/api/v1/auth/password/reset', { token: params.get('token'), password });
              setDone(true);
            } catch (err) {
              setError(err);
            }
          }}
        >
          <ErrorBox error={error} />
          <Field label="Nueva contraseña">{(id) => <PasswordInput id={id} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
          <Button type="submit" className="w-full">
            Guardar
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function SignupPage() {
  const { acceptTokens } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ companyName: '', adminName: '', email: '', password: '', acceptTerms: false, billingInterval: 'MONTHLY' as 'MONTHLY' | 'ANNUAL' });
  const { data: info } = useQuery({ queryKey: ['public-info'], queryFn: () => fetch('/api/v1/public/info').then((r) => r.json()), staleTime: 300_000 });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthShell title="Crea tu empresa en WulfTime" subtitle="Prueba gratuita de 30 días. Configurarás centros, horarios y trabajadores a continuación.">
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            const r = await api.post<{ accessToken: string }>('/api/v1/public/signup', { ...f, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid' });
            await acceptTokens(r);
            navigate('/admin');
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <ErrorBox error={error} />
        {info && (
          <div className="rounded-xl bg-brand-50 p-3 text-sm text-brand-900">
            <p className="font-semibold">{info.trialDays} días de prueba gratis</p>
            <p className="text-xs">{info.automaticPayment ? 'Al terminar el alta te pediremos un método de pago (tarjeta o domiciliación SEPA). Hoy no se cobra nada y puedes cancelar durante la prueba.' : 'Sin compromiso: puedes cancelar durante la prueba.'}</p>
          </div>
        )}
        <div role="radiogroup" aria-label="Forma de pago tras la prueba" className="grid grid-cols-2 gap-2">
          {(['MONTHLY', 'ANNUAL'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={f.billingInterval === k} onClick={() => setF({ ...f, billingInterval: k })} className={`rounded-lg border px-3 py-2 text-left text-sm ${f.billingInterval === k ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600' : 'border-slate-200'}`}>
              <span className="font-semibold">{k === 'MONTHLY' ? 'Mensual' : 'Anual'}</span>
              <span className="block text-xs text-slate-500">{k === 'MONTHLY' ? 'Renovación automática' : `${info?.annualDiscountPct ?? 10}% de descuento`}</span>
            </button>
          ))}
        </div>
        <Field label="Nombre de la empresa" required>{(id) => <Input id={id} value={f.companyName} onChange={(e) => setF({ ...f, companyName: e.target.value })} />}</Field>
        <Field label="Tu nombre" required>{(id) => <Input id={id} autoComplete="name" value={f.adminName} onChange={(e) => setF({ ...f, adminName: e.target.value })} />}</Field>
        <Field label="Email" required>{(id) => <Input id={id} type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />}</Field>
        <Field label="Contraseña" required hint="Mínimo 10 caracteres.">
          {(id) => <PasswordInput id={id} autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />}
        </Field>
        <label className="flex items-start gap-2 text-sm text-slate-600">
          <input type="checkbox" className="mt-1" checked={f.acceptTerms} onChange={(e) => setF({ ...f, acceptTerms: e.target.checked })} />
          <span>
            Acepto las{' '}
            <a href="/legal/condiciones" target="_blank" rel="noreferrer" className="text-brand-700 underline">
              condiciones del servicio
            </a>{' '}
            y el{' '}
            <a href="/legal/encargo" target="_blank" rel="noreferrer" className="text-brand-700 underline">
              contrato de encargo del tratamiento
            </a>
            . He leído la{' '}
            <a href="/legal/privacidad" target="_blank" rel="noreferrer" className="text-brand-700 underline">
              política de privacidad
            </a>
            .
          </span>
        </label>
        <Button type="submit" className="w-full" loading={busy}>
          Crear empresa
        </Button>
        <p className="text-center text-sm">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="text-brand-700 hover:underline">
            Inicia sesión
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function InvitePage() {
  const { token } = useParams();
  const { acceptTokens } = useAuth();
  const navigate = useNavigate();
  const { data, error, isLoading } = useQuery({ queryKey: ['invite', token], queryFn: () => api.get(`/api/v1/auth/invitations/${token}`), retry: false });
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [ack, setAck] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (isLoading) return <Spinner />;
  if (error) return <AuthShell title="Invitación no válida"><ErrorBox error={error} /></AuthShell>;
  return (
    <AuthShell title={`Únete a ${data.company}`} subtitle={data.existingAccount ? 'Ya tienes una cuenta con este email: confirma tu contraseña para unirte.' : 'Crea tu cuenta para empezar a fichar.'}>
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            const r = await api.post<{ accessToken: string }>(`/api/v1/auth/invitations/${token}/accept`, { fullName: fullName || undefined, password, acknowledgeGpsNotice: ack });
            await acceptTokens(r);
            navigate('/');
          } catch (er) {
            setErr(er);
          } finally {
            setBusy(false);
          }
        }}
      >
        <ErrorBox error={err} />
        <Field label="Email">{(id) => <Input id={id} value={data.email} disabled />}</Field>
        {!data.existingAccount && <Field label="Nombre completo">{(id) => <Input id={id} placeholder={data.employeeName ?? ''} value={fullName} onChange={(e) => setFullName(e.target.value)} />}</Field>}
        <Field label={data.existingAccount ? 'Tu contraseña actual' : 'Elige una contraseña'} hint={data.existingAccount ? undefined : 'Mínimo 10 caracteres.'}>
          {(id) => <PasswordInput id={id} autoComplete={data.existingAccount ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        {data.gpsNotice && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <p className="mb-1 flex items-center gap-1 font-semibold text-slate-800">
              <MapPin className="h-3.5 w-3.5" /> Información sobre geolocalización
            </p>
            <p className="whitespace-pre-line">{data.gpsNotice}</p>
            <label className="mt-2 flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} /> He leído la información sobre el uso de la ubicación.
            </label>
          </div>
        )}
        {data.require2fa && <Alert tone="info">Tu empresa exige verificación en dos pasos: la configurarás tras crear la cuenta.</Alert>}
        <Button type="submit" className="w-full" loading={busy}>
          {data.existingAccount ? 'Unirme' : 'Crear cuenta'}
        </Button>
      </form>
    </AuthShell>
  );
}

/** Configuración de 2FA (también la pantalla forzada cuando la empresa la exige). */
export function TwoFactorSetup({ onDone, forced }: { onDone?: () => void; forced?: boolean }) {
  const { acceptTokens } = useAuth();
  const toast = useToast();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const start = async () => {
    setError(null);
    try {
      const s = await api.post<{ secret: string; otpauthUrl: string }>('/api/v1/auth/2fa/setup');
      setSetup(s);
      setQr(await QRCode.toDataURL(s.otpauthUrl, { margin: 1, width: 200 }));
    } catch (e) {
      setError(e);
    }
  };

  if (codes)
    return (
      <div className="space-y-4">
        <Alert tone="success" title="Verificación en dos pasos activada">
          Guarda estos códigos de recuperación en un lugar seguro. Cada uno sirve una sola vez si pierdes el móvil.
        </Alert>
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-900 p-4 font-mono text-sm text-emerald-300">
          {codes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            navigator.clipboard?.writeText(codes.join('\n'));
            toast.success('Códigos copiados');
          }}
        >
          Copiar códigos
        </Button>
        <Button className="ml-2" onClick={onDone}>
          Continuar
        </Button>
      </div>
    );

  return (
    <div className="space-y-4">
      <ErrorBox error={error} />
      {!setup ? (
        <>
          <p className="text-sm text-slate-600">Usa una aplicación de autenticación (Google Authenticator, Microsoft Authenticator, 1Password…) para generar códigos de un solo uso.</p>
          <Button onClick={start} icon={<ShieldCheck className="h-4 w-4" />}>
            {forced ? 'Configurar ahora' : 'Activar verificación en dos pasos'}
          </Button>
        </>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const r = await api.post<{ recoveryCodes: string[]; accessToken: string }>('/api/v1/auth/2fa/confirm', { code });
              await acceptTokens(r);
              setCodes(r.recoveryCodes);
            } catch (er) {
              setError(er);
            }
          }}
        >
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
            {qr && <img src={qr} alt="Código QR para la aplicación de autenticación" className="h-44 w-44 rounded-lg border" />}
            <div className="text-sm text-slate-600">
              <p>1. Escanea el código QR con tu aplicación.</p>
              <p className="mt-2">O introduce esta clave manualmente:</p>
              <code className="mt-1 block break-all rounded bg-slate-100 p-2 text-xs">{setup.secret}</code>
              <p className="mt-2">2. Escribe el código de 6 dígitos que muestra la aplicación.</p>
            </div>
          </div>
          <Field label="Código de verificación">{(id) => <Input id={id} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="text-center text-lg tracking-[0.3em]" />}</Field>
          <Button type="submit" disabled={code.length !== 6}>
            Confirmar
          </Button>
        </form>
      )}
    </div>
  );
}

export function MfaSetupPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  return (
    <AuthShell title="Protege tu cuenta" subtitle="Tu empresa exige la verificación en dos pasos para tu rol.">
      <Card>
        <TwoFactorSetup forced onDone={() => navigate('/')} />
      </Card>
      <button className="mt-4 text-sm text-slate-500 hover:underline" onClick={() => logout().then(() => navigate('/login'))}>
        Cerrar sesión
      </button>
    </AuthShell>
  );
}

/**
 * Entrada desde la app móvil sin volver a iniciar sesión: canjea el código de un solo uso (en el fragmento #, que no
 * llega al servidor en la carga de la página) por una sesión web y abre la sección pedida (p. ej. el panel de empresa).
 */
export function HandoffPage() {
  const { acceptTokens, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [params] = useState(() => new URLSearchParams(window.location.hash.slice(1)));
  useEffect(() => {
    // Se espera a que termine la comprobación inicial de sesión, para que no pise la sesión nueva
    if (loading) return;
    const p = params;
    const code = p.get('code');
    const next = p.get('next') ?? '/';
    window.history.replaceState(null, '', '/handoff'); // el código no se queda en el historial
    if (!code) {
      setError('Enlace incompleto. Vuelve a abrirlo desde la app.');
      return;
    }
    api
      .post<{ accessToken: string }>('/api/v1/auth/web-handoff/exchange', { code })
      .then((r) => acceptTokens(r))
      .then(() => navigate(/^\/[a-z]/i.test(next) && !next.startsWith('//') ? next : '/', { replace: true }))
      .catch((e) => setError(e instanceof Error ? e.message : 'No se ha podido abrir la sesión.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <Logo />
      {error ? (
        <>
          <Alert tone="error">{error}</Alert>
          <Link to="/login" className="text-brand-700 hover:underline">
            Iniciar sesión
          </Link>
        </>
      ) : (
        <Spinner />
      )}
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <CheckCircle2 className="mb-4 h-10 w-10 text-slate-300" />
      <h1 className="text-xl font-semibold">Página no encontrada</h1>
      <Link to="/" className="mt-3 text-brand-700 hover:underline">
        Volver al inicio
      </Link>
    </div>
  );
}

export { ApiError };
